// @ts-nocheck
/* eslint-disable react/prop-types */
import React, { memo } from 'react';
import { Badge } from '@mui/material';

// Default configuration constants
const DEFAULT_COLOR = 'error';
const DEFAULT_VARIANT = 'standard';
const DEFAULT_MAX = 99;
const DEFAULT_INVISIBLE = false;
const DEFAULT_SHOW_ZERO = false;
const DEFAULT_OVERLAP = 'rectangular';
const DEFAULT_TEXT_COLOR = 'common.white';
const DEFAULT_ANCHOR_ORIGIN = { vertical: 'top', horizontal: 'right' };

// Standard MUI colors that the Badge component accepts natively
const MUI_STANDARD_COLORS = [
  'primary',
  'secondary',
  'error',
  'info',
  'success',
  'warning',
  'default',
];

const BADGE_STYLES = {
  '& .MuiBadge-standard': {
    minWidth: 18,
    height: 18,
    padding: '0 5px',
    borderRadius: '9px',
    fontSize: '0.7rem',
    fontWeight: 600,
    lineHeight: 1,
    boxSizing: 'border-box',
  },
};

/**
 * SolyticsBadge - A wrapper around MUI Badge component
 *
 * @component
 * @description
 * SolyticsBadge wraps the Material-UI Badge, providing custom default styling
 * to match the Solytics design system while enforcing strict prop exposure.
 *
 * @param {React.ReactNode} children - **DISCLAIMER:** Please DO NOT pass large pieces of JSX here! This should ideally only wrap simple icon components (like `<MailIcon />`) to keep the UI clean.
 * @param {React.ReactNode} badgeContent - The content rendered within the badge
 * @param {string} color - Accepts MUI colors, hex values, or theme paths (e.g. 'primary.main'). **NOTE**: To pass theme colors as strings or in Storybook, use dot-notation paths relative to the palette (like "primary.main" or "text.primary") rather than full JS objects.
 * @param {string} textColor - Optional text color for custom colored badges (accepts hex, rgb, or theme paths).
 * @param {string} variant - The variant to use: 'standard' | 'dot'
 * @param {boolean} invisible - If true, the badge is hidden
 * @param {number} max - Max count to show
 * @param {object} anchorOrigin - The anchor of the badge: { vertical: 'top'|'bottom', horizontal: 'left'|'right' }
 * @param {boolean} showZero - If true, the badge will show when badgeContent is zero
 * @param {string} overlap - Overlap behavior: 'rectangular' | 'circular'
 *
 * @returns {React.ReactElement} A badge element
 *
 * @example
 * // Standard number badge
 * <SolyticsBadge badgeContent={4} color="error">
 *   <MailIcon />
 * </SolyticsBadge>
 *
 * @example
 * // Dot variant
 * <SolyticsBadge variant="dot" color="primary">
 *   <Avatar />
 * </SolyticsBadge>
 */
const SolyticsBadge = React.forwardRef(
  (
    {
      children,
      badgeContent,
      color = DEFAULT_COLOR,
      textColor,
      variant = DEFAULT_VARIANT,
      invisible = DEFAULT_INVISIBLE,
      max = DEFAULT_MAX,
      showZero = DEFAULT_SHOW_ZERO,
      overlap = DEFAULT_OVERLAP,
      anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
      id,
    },
    ref
  ) => {
    const isStandardColor = MUI_STANDARD_COLORS.includes(color);

    return (
      <Badge
        ref={ref}
        color={isStandardColor ? color : 'default'}
        badgeContent={badgeContent}
        variant={variant}
        invisible={invisible}
        max={max}
        showZero={showZero}
        overlap={overlap}
        {...(id && { id })}
        sx={[
          BADGE_STYLES,
          !isStandardColor && {
            '& .MuiBadge-badge': {
              bgcolor: color,
              ...(!textColor && { color: DEFAULT_TEXT_COLOR }),
            },
          },
          textColor && {
            '& .MuiBadge-badge': {
              color: textColor,
            },
          },
        ]}
        anchorOrigin={anchorOrigin}
      >
        {children}
      </Badge>
    );
  }
);

SolyticsBadge.displayName = 'SolyticsBadge';

export default memo(SolyticsBadge);
